import { useState, useEffect, useMemo, Fragment } from 'react';
import {
  Route,
  Switch,
  Redirect,
  useLocation
} from 'react-router-dom';
import ReactGA from 'react-ga';

import './App.css';

import UserContext from './store/user-context';
import { ModalProvider } from './store/modal-context';
import Navigation from './components/Layout/Navigation';
import Welcome from './components/Pages/Welcome/Welcome';
import EmailConfirm from './components/Pages/Welcome/EmailConfirm';
import Town from './components/Pages/Town';
import ConversationLanding from './components/Pages/Conversation/ConversationLanding/ConversationLanding';
import Conversation from './components/Pages/Conversation';
import ResetPassword from './components/Pages/Welcome/Login/Reset/ResetPassword';
import Unsubscribe from './components/Pages/Unsubscribe/Unsubscribe';
import Confirmed from './components/Pages/Unsubscribe/Confirmed';
import { getLangCode, getLocalUser } from './components/Helpers/Util/User';
import NoMatch from './components/UI/NoMatch';
import ErrorBoundary from './components/UI/ErrorBoundary';
import { createRipple } from './components/UI/Button';
import Dashboard from './components/User/Dashboard/Dashboard';
import { useTranslation } from 'react-i18next';
import { languages as fallbackLangs } from './fallback-langs';

export let LANGUAGES = window['golingo'] ? window['golingo'].languages : fallbackLangs;

function App() {
  const { i18n } = useTranslation();
  let localUser = getLocalUser();
  const location = useLocation();
  const TRACKING_ID = 'UA-212831171-1';
  ReactGA.initialize(TRACKING_ID, {
    debug: false,
  });
  const [originalReferer] = useState(document.referer || '');

  useEffect(() => {
    if (location.pathname === '/dashboard-login') {
      localStorage.setItem('profileRedirect', JSON.stringify('1'));
    }
  }, []);
  
  useEffect(() => {
    const buttons = document.querySelectorAll(".button");
    for (const button of buttons) {
      button.addEventListener("click", createRipple);
    }
  });

  const [user, setUser] = useState(localUser);

  const value = useMemo(() => ({ user, setUser }), [user, setUser]);

  useEffect(() => {
    const updateLocalData = () => {
      localStorage.setItem('user', JSON.stringify(user));

      //ensure app is using users native lang
      i18n.changeLanguage(getLangCode(user.user.language));
    };

    if (user !== null) {
      updateLocalData();
    }
  }, [user]);

  let showWelcome = true;

  if (user !== null) {
    if (Object.prototype.hasOwnProperty.call(user, 'confirmed')) {
      if (user.confirmed !== true) {
        showWelcome = true;
      } else {
        showWelcome = false;
      }
    }

    if (
      !Object.prototype.hasOwnProperty.call(user, 'user') ||
      typeof user !== 'object'
    ) {
      alert(
        'Critical Error whilst loading user! Please contact administrators',
      );
      setUser(null);
    }

    //User object MUST be set to use app
    if (Object.prototype.hasOwnProperty.call(user, 'user')) {
      if (user.user === null) {
        alert(
          'Critical Error whilst loading user! Please contact administrators',
        );
        setUser(null);
      }
    }
  }

  const rebootServiceWorkers = () => {
    const channel = new BroadcastChannel('golingo-service-worker-channel');
    channel.postMessage({ skipWaiting: true });
    window.location.reload(true);
  };

  let content = showWelcome ? (
    <Fragment>
      <Switch>
        <Route exact path="/">
          <Welcome originalReferer={originalReferer} />
        </Route>
        <Route exact path="/login">
          <Welcome originalReferer={originalReferer} />
        </Route>
        <Route exact path="/register">
          <Welcome originalReferer={originalReferer} />
        </Route>
        <Route exact path="/dashboard-login">
          <Welcome originalReferer={originalReferer} />
        </Route>
        <Route exact path="/confirm-email">
          <EmailConfirm />
        </Route>
        <Route exact path="/unsubscribe">
          <Unsubscribe />
        </Route>
        <Route exact path="/unsubscribe/confirmed">
          <Confirmed />
        </Route>
        <Route exact path="/forgottenpass">
          <ResetPassword />
        </Route>
        {/* redirect to home page if on any other page */}
        <Redirect from="*" to="/" />
      </Switch>
    </Fragment>
  ) : (
    <Fragment>
      <Navigation />
      <Switch>
        <Route exact path="/">
          <Town />
        </Route>
        <Route exact path="/conversation/:location">
          <ConversationLanding />
        </Route>
        <Route
          exact
          path="/conversation/:location/:scene_id/:lesson"
          key={Date.now()}>
          <Conversation />
        </Route>
        <Route exact path="/user/dashboard">
          <Dashboard />
        </Route>
        <Route exact path="/unsubscribe">
          <Unsubscribe />
        </Route>
        <Route exact path="/unsubscribe/confirmed">
          <Confirmed />
        </Route>
        <Route exact path="/forgottenpass">
          <ResetPassword />
        </Route>
        <Redirect from="/login" to="/" />
        <Redirect from="/register" to="/" />
        <Route path="*">
          <NoMatch />
        </Route>
      </Switch>
    </Fragment>
  );

  return (
    <ErrorBoundary>
      <UserContext.Provider value={value}>
        <ModalProvider>
          {content}
        </ModalProvider>
        <div
          id="pwa-updated"
          onClick={rebootServiceWorkers}
          style={{ display: 'none' }}>
          <p>Woah! A new version is available click here to reload the app!</p>
        </div>
    </UserContext.Provider>
    </ErrorBoundary>
  );
}

export default App;
